import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Alert, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Tooltip } from '@mui/material';
import StatusChip from '../StatusChip';
import api from '../../services/api';
import { useToast } from '../ToastProvider';
import { toYyyyMmDd } from '../../utils/businessDays';
import EmptyState from '../ui/EmptyState';
import ErrorBanner from '../ui/ErrorBanner';
import SkeletonBlock from '../ui/SkeletonBlock';
import { usePermissions } from '../PermissionsContext';
import { useConfirm } from '../Common/ConfirmDialog';

export default function MyLeaveRequests() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('hrapp_lr_filter') || 'All');
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [balances, setBalances] = useState([]);
  const { isSystemAdmin } = usePermissions();

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [reqs, bals] = await Promise.all([
        api.get('/leave-requests/mine'),
        api.get('/leave-balances/mine')
      ]);
      const arr = Array.isArray(reqs) ? reqs : [];
      const balArr = Array.isArray(bals) ? bals : [];
      setBalances(balArr);
      setItems(enrichWithAvailableRemaining(arr, balArr));
    } catch (e) {
      const msg = e?.response?.data || e.message; setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resend = async (id) => {
    try {
      await api.post(`/leave-requests/${id}/resend`);
      toast.success('Reminder sent to approvers');
    } catch (e) { toast.error(e?.response?.data || e.message); }
  };

  const enrichWithAvailableRemaining = (reqs, balArr) => {
    // Prepare per-year AL balance from server; use Remaining (without reservations) as base
    const alByYear = new Map();
    (balArr||[]).forEach(b => {
      const code = String(b.code||b.Code||'').toUpperCase();
      const year = Number(b.year || b.Year);
      if (code === 'AL' && year) {
        const remainingDays = Number(
          (b.remainingDays ?? b.RemainingDays ?? 0)
        );
        alByYear.set(year, { remainingDays });
      }
    });

    // Sort by RequestDate asc (по изискване)
    const sorted = [...reqs].sort((a,b)=> new Date(a.requestDate||a.RequestDate) - new Date(b.requestDate||b.RequestDate));

    // Кумилираме "остатък след" върху Remaining (без резервите)
    const remainingByYear = new Map();

    return sorted.map(r => {
      const y = new Date(r.startDate||r.StartDate).getFullYear();
      let actual = remainingByYear.has(y) ? remainingByYear.get(y) : (alByYear.get(y)?.remainingDays);
      if (typeof actual !== 'number') actual = undefined;

      const days = Number(r.businessDays||r.BusinessDays||0);
      const status = (r.status||r.Status);

      const availableAtStart = actual;
      let remainingAfter = actual;
      if (typeof actual === 'number') {
        const isPending = ['Pending','PendingDM','PendingBoard'].includes(String(status));
        // От Remaining изваждаме кумулативно само висящите заявки
        if (isPending) {
          remainingAfter = Math.max(0, actual - days);
          remainingByYear.set(y, remainingAfter);
        } else {
          remainingByYear.set(y, actual);
        }
      }

      return { ...r, _availableAtStart: availableAtStart, _remainingAfter: remainingAfter };
    });
  };

  const cancel = async (id) => {
    const reason = window.prompt('Reason for cancellation (required):');
    if (!reason) return;

    try {
      await api.post(`/leave-requests/${id}/request-cancel`, { reason: reason });
      setItems(prev => prev.map(r => (r.id||r.Id)===id ? { ...r, status:'Cancelled', Status:'Cancelled' } : r));
      toast.success('Request cancelled');
    } catch (e) {
      toast.error(e?.response?.data || e.message);
    }
  };

  const requestCancel = async (id) => {
    const reason = window.prompt('Reason for cancel request (required):');
    if (!reason) return;
    try {
      await api.post(`/leave-requests/${id}/request-cancel`, { reason: reason });
      setItems(prev => prev.map(r => (r.id||r.Id)===id ? { ...r, status:'CancelRequested', Status:'CancelRequested', cancelReason: reason, CancelReason: reason } : r));
      toast.success('Cancel requested');
    } catch (e) { toast.error(e?.response?.data || e.message); }
  };

  // Group all pending statuses together when filtering
  const pendingStatuses = ['Pending', 'PendingDM', 'PendingBoard'];

  const filtered = items.filter(r => {
    if (statusFilter === 'All') return true;
    const st = (r.status || r.Status || '').toString();
    // "Pending (All)" matches any pending status
    if (statusFilter === 'Pending') {
      return pendingStatuses.includes(st);
    }
    return st === statusFilter;
  });

  return (
    <Box>
      <Box sx={{ display:'flex', gap:2, mb:1 }}>
        <label>
          Status:
          <select value={statusFilter} onChange={(e)=>{ setStatusFilter(e.target.value); sessionStorage.setItem('hrapp_lr_filter', e.target.value); }} style={{ marginLeft:8 }}>
            <option value="All">All</option>
            <option value="Pending">Pending (All)</option>
            <option value="PendingDM">Pending DM</option>
            <option value="PendingBoard">Pending Board</option>
            <option value="Approved">Approved</option>
            <option value="CancelRequested">Cancel Requested</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Rejected">Rejected</option>
          </select>
        </label>
      </Box>
      {error && <ErrorBanner message={error} onClose={()=>setError('')} />}
      {loading ? <SkeletonBlock rows={6} /> : (
        filtered.length === 0 ? (
          <EmptyState title={statusFilter==='All'?"No leave requests":"No requests in this status"} description={statusFilter==='All'?"You have not submitted any leave requests yet.":`No requests with status ${statusFilter}.`} actionLabel={statusFilter==='All'?"Create Request":undefined} onAction={statusFilter==='All'?()=>window.dispatchEvent(new CustomEvent('hrapp.open.new.request')):undefined} />
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell align="right">Business Days</TableCell>
                  <TableCell align="right">Available at start</TableCell>
                  <TableCell align="right">Remaining after</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => {
                  const status = r.status || r.Status;
                  const id = r.id || r.Id;
                  const reqDate = new Date(r.requestDate || r.RequestDate || r.startDate || r.StartDate);
                  const ageHours = (Date.now() - reqDate.getTime()) / 36e5;
                  const canResend = pendingStatuses.includes(status) && ageHours >= 48;
                  return (
                    <TableRow key={id} hover>
                      <TableCell>{'Paid Leave'}</TableCell>
                      <TableCell>{toYyyyMmDd(new Date(r.startDate||r.StartDate))}</TableCell>
                      <TableCell>{toYyyyMmDd(new Date(r.endDate||r.EndDate))}</TableCell>
                      <TableCell align="right">{r.businessDays ?? r.BusinessDays}</TableCell>
                      <TableCell align="right">{typeof r._availableAtStart === 'number' ? r._availableAtStart : '-'}</TableCell>
                      <TableCell align="right">{typeof r._remainingAfter === 'number' ? r._remainingAfter : '-'}</TableCell>
                      <TableCell><StatusChip status={status} /></TableCell>
                      <TableCell align="right">
                        {(['Pending','PendingDM','PendingBoard'].includes(status)) && (
                          <>
                            <Button size="small" onClick={() => cancel(id)}>Cancel</Button>
                            {canResend && <Button size="small" onClick={() => resend(id)} sx={{ ml: 1 }}>Re-send</Button>}
                          </>
                        )}
                        {(status === 'Approved') && (
                          <Button size="small" color="warning" onClick={() => requestCancel(id)} sx={{ ml: 1 }}>Request Cancel</Button>
                        )}
                        {isSystemAdmin && (
                          <Button size="small" color="error" onClick={async()=>{
                            if(!(await confirm({ message: 'Delete this request permanently?', danger: true, confirmLabel: 'Delete' }))) return;
                            try { await api.delete(`/leave-requests/${r.id||r.Id}`); setItems(prev=>prev.filter(x => (x.id||x.Id)!==(r.id||r.Id))); toast.success('Deleted'); }
                            catch(e){ toast.error(e?.response?.data || e.message); }
                          }}>Delete</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
    </Box>
  );
}

